"use client";

import { isEmpty } from "lodash";
import moment from "moment";
import React, { useCallback } from "react";
import { toast } from "react-toastify";
import { Button, Spinner } from "reactstrap";
import {
  BotIcon,
  CustomerChatLulaIcon,
  XCloseIcon,
} from "src/assets/images/job-details-slide-out/job-details-slide-out";
import { createAbbreviation } from "src/helpers/utils";
import { wrapStringWithLink } from "src/helpers/message-utils";
import { useAppDispatch, useAppSelector } from "src/hooks/redux-hooks";
import { markAsReadAndUnreadAction, sendMessage } from "src/redux/jobs/api";
import {
  updateMessageToReadOrUnread,
  updateReduxMessageState,
} from "src/redux/jobs/slice";
import { updateVisibility } from "src/redux/modals/slice";

interface MessageAttachmentItem {
  attachment_id?: number;
  media_id?: string;
  attachment_local_url?: string;
  thumbnail_url?: string;
  attachment_optimized_url?: string;
  attachment_url?: string;
  [key: string]: unknown;
}

interface SenderDetails {
  name?: string;
  type?: string;
  profile_url?: string;
  [key: string]: unknown;
}

interface MessageContentData {
  message_id: number;
  message?: string;
  template?: Record<string, unknown> | null;
  direction?: string;
  type?: string;
  created_at?: string;
  sender_details?: SenderDetails;
  attachments?: MessageAttachmentItem[];
  can_toggle_message_status?: boolean;
  isSending?: boolean;
  hasFailed?: boolean;
  source?: string;
  [key: string]: unknown;
}

interface MessageProps {
  messageContent: MessageContentData;
  dateGroup: string;
  key: number;
}

const Message: React.FC<MessageProps> = ({
  messageContent,
  dateGroup,
  key,
}) => {
  const dispatch = useAppDispatch();

  const activeThreadId = useAppSelector(
    (state) =>
      (state?.jobDetails?.messages as Record<string, unknown>)?.activeThreadId,
  ) as number | undefined;
  const jobId = useAppSelector(
    (state) => state?.jobDetails?.data?.root_node_id,
  ) as string | undefined;
  const serviceRequestId = useAppSelector(
    (state) => state?.jobDetails?.data?.service_request_id,
  );
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const timeZone = useAppSelector(
    (state) => state?.jobDetails?.data?.timezone,
  ) as string | undefined;

  const unreadChatIds = useAppSelector(
    (state) =>
      (state?.jobDetails?.messages as Record<string, unknown>)?.unread_chat_ids,
  ) as Array<string | number> | undefined;
  const isUnread = unreadChatIds?.includes(
    messageContent?.message_id as string | number,
  );

  const attachmentClickHandler = (
    attachments: MessageAttachmentItem[],
    selectedAttachment: MessageAttachmentItem,
  ) => {
    const imagesArray = (attachments || [])?.filter(
      (attachment) =>
        attachment?.attachment_id !== selectedAttachment?.attachment_id,
    );
    imagesArray.unshift(selectedAttachment);

    dispatch(
      updateVisibility({
        modalType: "imageSlidesModal",
        isVisible: true,
        extraProps: {
          modalData: {
            collection: imagesArray,
            showDeleteButton: false,
          },
        },
      }),
    );
  };

  const renderAvatar = useCallback((data: MessageContentData) => {
    if (data?.type === "system") {
      return <BotIcon />;
    }
    if (data?.direction === "inbound") {
      return <CustomerChatLulaIcon />;
    }
    if (data?.sender_details?.profile_url) {
      return <img src={data?.sender_details?.profile_url} alt="" />;
    }
    return createAbbreviation(data?.sender_details?.name);
  }, []);
  const markAsReadOrUnread = useCallback(
    (id: number, action: string, dateKey: string) => {
      dispatch(
        markAsReadAndUnreadAction(
          type === "jobDetails" ? jobId?.toString() : serviceRequestId?.toString(),
          id,
          action,
        )({}),
      )
        .unwrap()
        .then(() => {
          dispatch(
            updateMessageToReadOrUnread({
              id: id,
              messageAction: action as "mark-as-read" | "mark-as-unread",
              date: dateKey,
              activeThreadId:
                activeThreadId != null ? String(activeThreadId) : "",
            }),
          );
        })
        .catch((e) => {
          toast(
            e?.errors?.general?.[0] ||
              "Something went wrong",
            { type: "error" },
          );
        });
    },
    [activeThreadId, dispatch, jobId, serviceRequestId, type],
  );

  const onRetry = useCallback(
    (data: MessageContentData) => {
      dispatch(
        updateReduxMessageState({
          data: {
            isSending: true,
            hasFailed: false,
          },
          actionType: "update",
          date: "Today",
          tempId:
            data?.message_id != null ? String(data.message_id) : undefined,
        }),
      );

      const attachmentIds = data?.attachments?.map(
        (attachment) => attachment?.attachment_id,
      );

      const threadId =
        activeThreadId != null ? String(activeThreadId) : undefined;
      dispatch(
        sendMessage(
          serviceRequestId,
          threadId,
        )({
          message: data?.message,
          attachments: attachmentIds,
          activeThreadId,
        }),
      )
        .unwrap()
        .then((rawRes: unknown) => {
          const res = rawRes as Record<string, unknown>;
          const hasAttachments = !isEmpty(res.attachments);
          const updatedMessage = {
            ...res,
            message_id: data?.message_id,
            isSending: false,
            hasFailed: false,
            ...(hasAttachments && {
              attachments: data?.attachments,
            }),
          };
          dispatch(
            updateReduxMessageState({
              data: updatedMessage,
              date: "Today",
              actionType: "update",
              tempId:
                data?.message_id != null ? String(data.message_id) : undefined,
            }),
          );
        })
        .catch((error: Record<string, unknown>) => {
          dispatch(
            updateReduxMessageState({
              data: {
                message: data?.message,
                message_id: data?.message_id,
                isSending: false,
                hasFailed: true,
              },
              date: "Today",
              actionType: "update",
              tempId:
                data?.message_id != null ? String(data.message_id) : undefined,
            }),
          );
          toast.error((error as { message?: string }).message);
        });
    },
    [activeThreadId, dispatch, serviceRequestId],
  );

  return (
    <div
      className={`
          message-block
          ${messageContent?.direction}
          ${messageContent?.sender_details?.type === "Manager" ? "manager" : ""}
          ${messageContent?.sender_details?.type === "Tenant" ? "tenant" : ""}
          ${
            messageContent?.direction === "inbound" &&
            messageContent?.type === "system"
              ? "system"
              : ""
          }
          ${messageContent?.hasFailed ? "failed" : ""}
          ${messageContent?.isSending ? "sending" : ""}
          `}
      key={key}
    >
      <div className="message-wrap">
        <div className="message-avatar">{renderAvatar(messageContent)}</div>
        <div className="message-contents">
          <div className="message-head">
            <div className="messager-wrapper">
              <div className="messager-name">
                {messageContent?.sender_details?.name || ""}
              </div>
              {isUnread ? <div className="messager-status" /> : ""}

              {messageContent?.sender_details?.type && (
                <div className="messager-type">
                  {messageContent?.sender_details?.type}
                </div>
              )}
              {messageContent?.can_toggle_message_status &&
              messageContent?.message_id ? (
                <div className="messager-read-status">
                  <Button
                    className="clear-btn"
                    color="deco"
                    onClick={() =>
                      markAsReadOrUnread(
                        messageContent?.message_id,
                        isUnread ? "mark-as-read" : "mark-as-unread",
                        dateGroup,
                      )
                    }
                  >
                    {isUnread ? "Mark as read" : "Mark as Unread"}
                  </Button>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
          {/* Toggle classname "has-image" when message has image  */}
          <div
            className={`message-body ${
              (messageContent?.attachments?.length ?? 0) > 0 ? "has-image" : ""
            }`}
          >
            {/* Toggle classname "single-preview" when only one image is present  */}
            {(messageContent?.attachments?.length ?? 0) > 0 && (
              <div
                className={`preview-image-wrapper ${
                  messageContent?.attachments?.length === 1
                    ? "single-preview"
                    : ""
                } `}
              >
                {messageContent?.attachments?.map((image) => {
                  const getImageUrl = () =>
                    image?.attachment_local_url ||
                    image?.thumbnail_url ||
                    image?.attachment_optimized_url ||
                    image?.attachment_url;
                  return (
                    <div
                      className="preview-image"
                      key={image?.attachment_id || image?.media_id}
                    >
                      <img
                        src={getImageUrl()}
                        alt=""
                        onClick={() =>
                          attachmentClickHandler(
                            messageContent?.attachments as MessageAttachmentItem[],
                            image,
                          )
                        }
                        role="presentation"
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <div>
              {wrapStringWithLink({
                message: messageContent?.message,
                template: messageContent?.template as Parameters<
                  typeof wrapStringWithLink
                >[0]["template"],
              })}
            </div>
          </div>
          {(messageContent?.created_at || !isEmpty(messageContent?.source)) && (
            <div className="messager-time-wrapper">
              {!isEmpty(messageContent?.source) && (
                <React.Fragment>
                  <div className="messager-origin">
                    {messageContent?.source}
                  </div>
                  <div className="messager-separator" />
                </React.Fragment>
              )}
              {messageContent?.created_at && timeZone ? (
                <div className="messager-time">
                  {moment
                    ?.utc(messageContent?.created_at)
                    ?.tz(timeZone)
                    ?.format("h:mmA z") || ""}
                </div>
              ) : (
                ""
              )}
            </div>
          )}
          <div className="message-status-wrapper">
            {messageContent?.isSending ? (
              <div className="message-status">
                <Spinner />
                Sending...
              </div>
            ) : (
              ""
            )}

            {messageContent?.hasFailed ? (
              <React.Fragment>
                <div className="message-status">
                  <XCloseIcon />
                  Sending Failed
                </div>
                <button
                  className="retry-btn"
                  type="button"
                  onClick={() => onRetry(messageContent)}
                >
                  Retry
                </button>
              </React.Fragment>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Message;
